import { useRouter, useRouterState, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Site-wide back control. Uses browser history when the visitor arrived from
 * another page on the site, so state (e.g. an in-progress team squad, filters,
 * scroll position) is preserved. Falls back to Home on a cold entry.
 *
 * History depth is only known in the browser, so the server and the first
 * client render always emit the "Back to home" link; the history button
 * appears after hydration.
 */
export function BackButton() {
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const historyIndex = useRouterState({
    select: (s) => (s.location.state as { __TSR_index?: number } | undefined)?.__TSR_index ?? 0,
  });
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);

  if (pathname === "/") return null;

  const canGoBack = hydrated && historyIndex > 0;
  const cls =
    "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-card hover:text-foreground transition-colors";

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-4">
      {canGoBack ? (
        <button type="button" onClick={() => router.history.back()} className={cls} aria-label="Go back to the previous page">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
      ) : (
        <Link to="/" className={cls} aria-label="Back to home">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to home
        </Link>
      )}
    </div>
  );
}

